import { useState } from "react";
import { Link } from "react-router-dom";
import { ChefHat, Loader2, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function formatDetail(detail) {
  if (!detail) return "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("Reset link sent if that email is registered.");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Could not send reset link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="forgot-password-page">
      <div className="relative hidden md:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1551218808-94e220e084d2?auto=format&fit=crop&w=1600&q=80"
          alt="Kitchen"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/50 to-black/70" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-foreground">
          <div className="label-eyebrow gold mb-4">Account recovery</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[1.02] mb-4 tracking-tight">
            Lost your key.<br /><span className="gold">Not your kitchen.</span>
          </h1>
          <p className="text-foreground/70 max-w-md text-sm">
            Enter the email attached to your account. We will send a single-use link, valid for 30 minutes.
          </p>
        </div>
      </div>

      <div className="relative flex items-center justify-center p-6 md:p-12">
        <div className="glass w-full max-w-sm rounded-[28px] p-8 md:p-10" data-testid="forgot-glass-panel">
          <div className="flex items-center gap-3 mb-10">
            <img
              src="/brand/mark-256.png"
              alt="CulinaireSpecialist"
              className="w-10 h-10 rounded-full object-contain"
              style={{ background: "#0a0806", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.15), 0 0 0 1px rgba(232,207,154,0.35), 0 4px 12px rgba(200,169,106,0.22)" }}
            />
            <div>
              <div className="font-display text-xl leading-tight text-white/95">CulinaireSpecialist</div>
              <div className="text-[0.55rem] uppercase tracking-[0.28em] mt-0.5" style={{ color: "rgba(232,207,154,0.85)" }}>
                Reset password
              </div>
            </div>
          </div>

          {sent ? (
            <div data-testid="forgot-sent-state">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5" style={{ color: "#c8a96a" }} />
                <h2 className="font-display text-2xl text-white/95">Check your inbox.</h2>
              </div>
              <p className="text-sm text-white/60 mb-6 leading-relaxed">
                If <span className="text-white/90">{email}</span> is registered, a reset link is on its way. It expires in 30 minutes and can be used only once.
              </p>
              <p className="text-xs text-white/45 mb-8 leading-relaxed">
                No email after a couple of minutes? Check your spam folder, or try again with a different address.
              </p>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setSent(false); setEmail(""); }}
                  className="text-white/60 hover:text-white/90 transition-colors"
                  data-testid="forgot-try-again"
                >
                  Try another email
                </button>
                <Link
                  to="/login"
                  className="font-medium hover:brightness-110 transition-all"
                  style={{ color: "#f2dfb5" }}
                  data-testid="forgot-back-to-login"
                >
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <>
              <h2 className="font-display text-3xl text-white/95 mb-2">Reset your password.</h2>
              <p className="text-sm text-white/55 mb-8">Enter your email. We will send you a fresh link.</p>

              <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-xs text-white/70">Email</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/45 pointer-events-none" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="glass-input w-full h-10 pl-9 pr-3 rounded-lg text-sm text-white/95"
                      data-testid="forgot-email"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="gold-cta group w-full h-11 rounded-lg flex items-center justify-center gap-2 text-sm font-semibold disabled:opacity-60"
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      Send reset link
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </form>

              <p className="text-sm text-white/50 mt-6">
                Remembered it?{" "}
                <Link
                  to="/login"
                  className="font-medium hover:brightness-110 transition-all"
                  style={{ color: "#f2dfb5" }}
                  data-testid="forgot-link-login"
                >
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
